import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, FolderOpen, Clock, ArrowLeft, Search, Archive } from "lucide-react";
import { RetentionBanner } from "@/components/LocalVault";

const STATE_LABEL = {
  NEW: "Novo",
  UPLOADING: "Enviando arquivos",
  UPLOADED: "Arquivos enviados",
  PREPROCESSING: "Pré-processando",
  OCR_PROCESSING: "OCR em execução",
  CLASSIFYING: "Classificando docs",
  PROCESS_TIMELINE_BUILDING: "Montando timeline",
  TITLE_BUILDING: "Consolidando título",
  TITLE_REVIEW: "Título p/ revisão",
  PARAMETER_EXTRACTION: "Extraindo parâmetros",
  PARAMETER_REVIEW: "Parâmetros p/ revisão",
  WAITING_USER_INPUT: "Aguardando você",
  READY_FOR_CALCULATION: "Pronto p/ cálculo",
  CALCULATING: "Calculando",
  CALCULATION_VALIDATION: "Validando matemática",
  LEGAL_VALIDATION: "Validando juridicamente",
  ADVERSARIAL_REVIEW: "Revisão adversarial",
  REVIEW_REQUIRED: "Revisão necessária",
  READY_FOR_HUMAN_APPROVAL: "Pronto p/ aprovação",
  APPROVED: "Aprovado",
  EXPORTING: "Exportando",
  EXPORTED: "Exportado",
  ARCHIVED: "Arquivado",
  FAILED: "Falhou",
};

const STATE_COLOR = (s) => {
  if (s === "APPROVED" || s === "EXPORTED") return "bg-emerald-100 text-emerald-800";
  if (s === "FAILED") return "bg-rose-100 text-rose-800";
  if (s === "WAITING_USER_INPUT" || s === "REVIEW_REQUIRED") return "bg-amber-100 text-amber-800";
  if (s === "NEW") return "bg-slate-100 text-slate-700";
  return "bg-blue-100 text-blue-800";
};

export default function LiquidacaoList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [retention, setRetention] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (archived) params.set("archived", "true");
      const r = await api.get(`/liq/cases?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [archived]);
  useEffect(() => {
    api.get("/liq/retention/policy").then(r => setRetention(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6" data-testid="liq-list">
      <div className="max-w-6xl mx-auto space-y-4">
        <RetentionBanner retention={retention} />

        <Link to="/calculos" data-testid="liq-back-to-calculos"
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao Cálculos
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Hermes Calc · Beta
            </div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">
              Liquidação Trabalhista
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Envie o processo inteiro, deixe o Hermes ler, consolidar e
              perguntar apenas o essencial. Cada liquidação é versionada e
              rastreável até a última fórmula.
            </p>
          </div>
          <button
            onClick={() => nav("/calculos/liquidacao-trabalhista/nova")}
            data-testid="liq-new-btn"
            className="h-11 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium inline-flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4"/> Nova Liquidação
          </button>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3">
            <Search className="w-4 h-4 text-slate-400"/>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && load()}
              placeholder="Buscar por número, reclamante ou reclamada..."
              className="flex-1 h-11 bg-transparent outline-none text-sm"
              data-testid="liq-search-input"
            />
            <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900"
              data-testid="liq-search-btn">Buscar</button>
          </div>
          <button
            onClick={() => setArchived(v => !v)}
            data-testid="liq-toggle-archived"
            className={`h-11 px-4 rounded-xl border text-sm inline-flex items-center gap-2 ${
              archived ? "bg-slate-900 text-white border-slate-900"
                       : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}
          >
            <Archive className="w-4 h-4"/> {archived ? "Ver ativos" : "Ver arquivados"}
          </button>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="text-sm text-slate-500" data-testid="liq-loading">Carregando…</div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center"
                 data-testid="liq-empty">
              <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
              <div className="text-slate-700 font-medium">
                {archived ? "Nenhuma liquidação arquivada." : "Você ainda não criou uma liquidação."}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                Comece enviando o processo — o Hermes cuida do resto.
              </div>
              {!archived && (
                <button
                  onClick={() => nav("/calculos/liquidacao-trabalhista/nova")}
                  className="mt-4 h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2"
                  data-testid="liq-empty-cta"
                >
                  <Plus className="w-4 h-4"/> Nova Liquidação
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="liq-rows">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 px-1">
                {total} liquidação{total === 1 ? "" : "es"}
              </div>
              {rows.map(c => (
                <button
                  key={c.id}
                  onClick={() => nav(`/calculos/liquidacao-trabalhista/${c.id}`)}
                  data-testid={`liq-row-${c.id}`}
                  className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-400 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-sm text-slate-900 truncate">
                          {c.process_number}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${STATE_COLOR(c.state)}`}>
                          {STATE_LABEL[c.state] || c.state}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600 truncate">
                        <b>{c.claimant?.name || "—"}</b>
                        <span className="mx-1.5 text-slate-400">×</span>
                        {c.defendant?.name || "—"}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>{c.court || "—"}</span>
                        {c.uf && <span>· {c.uf}</span>}
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3"/>
                          {new Date(c.updated_at).toLocaleDateString("pt-BR")}
                        </span>
                        <span>{c.progress?.documents_uploaded ?? 0} doc(s)</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
